import { Box, Grid2 as Grid, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ProductDetail } from '../../api/business/types';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../../components/format';
import { HtmlPreview } from '../../components/HtmlPreview';
import { LanguageTabs } from '../../components/LanguageTabs';
import { saleState } from '../../components/prices';
import type { SaleState } from '../../components/prices';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/sk';

type ProductPropertiesSectionProps = {
  product: ProductDetail;
  currency: string;
};

const SALE_STATES: Record<SaleState, MessageKey> = {
  NONE: 'products.saleNone',
  ALWAYS: 'products.saleAlways',
  ENDED: 'products.saleEnded',
  RUNNING: 'products.saleRunning',
  COMING: 'products.saleComing',
};

const Item = ({ label, children }: { label: string; children: ReactNode }) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Grid>
);

/** What a shop shows of the product (R0030 WP-05): its texts per language, codes, prices, measures, display, stock. */
export const ProductPropertiesSection = ({ product, currency }: ProductPropertiesSectionProps) => {
  const { language, t } = useLanguage();
  const { languages } = useContentLanguages();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const texts = product.translations[current];
  const money = (minor: number | null): string => formatMoney(minor, currency, language);
  const amount = (value: number | null, unit: string): string =>
    value === null ? t('common.none') : `${formatNumber(value, language)} ${unit}`;
  const state = saleState(product.salePriceMinor, product.saleFrom, product.saleTo, new Date());

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('products.properties')}
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="text.secondary">
          {t('products.section.texts')}
        </Typography>
        {languages.length > 1 ? (
          <LanguageTabs
            languages={languages}
            value={current}
            onChange={setActive}
            missing={(code) =>
              !product.translations[code]?.name ||
              !product.translations[code]?.shortDescription ||
              !product.translations[code]?.description
            }
          />
        ) : null}
        <Typography variant="subtitle1">{texts?.name ?? t('common.none')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
          /{texts?.slug ?? '—'}
        </Typography>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('field.shortDescription')}
          </Typography>
          <HtmlPreview html={texts?.shortDescription ?? ''} emptyText={t('products.noDescription')} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('field.description')}
          </Typography>
          <HtmlPreview html={texts?.description ?? ''} emptyText={t('products.noDescription')} />
        </Box>
      </Stack>

      <Typography variant="overline" color="text.secondary">
        {t('products.section.prices')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Item label={t('products.regularPrice')}>
          {money(product.defaultPriceMinor)}
          {product.netPriceMinor !== null ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {t('products.netPrice', { price: money(product.netPriceMinor) })}
            </Typography>
          ) : null}
        </Item>
        <Item label={t('field.vatRate')}>{formatPercent(product.vatRateBasisPoints, language)}</Item>
        <Item label={t('products.priceNow')}>{money(product.effectivePriceMinor)}</Item>
        <Item label={t('products.onSale')}>
          {state === 'NONE'
            ? t('products.saleNone')
            : `${money(product.salePriceMinor)} · ${t(SALE_STATES[state])}`}
          {product.saleFrom !== null || product.saleTo !== null ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('products.saleWindow', {
                from: formatDate(product.saleFrom, language),
                to: formatDate(product.saleTo, language),
              })}
            </Typography>
          ) : null}
        </Item>
        <Item label={t('field.costPrice', { currency })}>{money(product.costPriceMinor)}</Item>
      </Grid>

      <Typography variant="overline" color="text.secondary">
        {t('products.section.codes')} · {t('products.section.measures')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Item label={t('field.mpn')}>{product.mpn ?? t('common.none')}</Item>
        <Item label={t('field.weightGrams')}>{amount(product.weightGrams, 'g')}</Item>
        <Item label={t('products.section.measures')}>
          {product.widthMm === null && product.heightMm === null && product.depthMm === null
            ? t('common.none')
            : t('products.dimensions', {
                width: product.widthMm ?? '—',
                height: product.heightMm ?? '—',
                depth: product.depthMm ?? '—',
              })}
        </Item>
      </Grid>

      <Typography variant="overline" color="text.secondary">
        {t('products.section.display')} · {t('products.section.stock')}
      </Typography>
      <Grid container spacing={2}>
        <Item label={t('field.visibility')}>{t(`visibility.${product.visibility}`)}</Item>
        <Item label={t('products.featured')}>{product.featured ? t('common.yes') : t('common.no')}</Item>
        <Item label={t('field.backorders')}>{t(`backorders.${product.backorders}`)}</Item>
        <Item label={t('field.lowStockThreshold')}>
          {product.lowStockThreshold === null
            ? t('common.none')
            : formatNumber(product.lowStockThreshold, language)}
        </Item>
        <Item label={t('field.minOrderQuantity')}>{formatNumber(product.minOrderQuantity, language)}</Item>
      </Grid>
    </Paper>
  );
};
